import type { ReactNode } from 'react'
import { formatBoardDate, numberValue, record, records, textValue, type ReportingPayload } from './reporting-model'
import {
  boardChartDefinition,
  businessAreaRows,
  donutSegmentPath,
  governancePositionChartMode,
  governancePositionChartRows,
  governanceWorkloadRows,
  materialChangeRows,
  niceMaximum,
  riskCategoryRows,
  roadmapActivityPoints,
  roadmapChartPoints,
  strategicAlignmentRows,
  wrapBoardLabel,
  type BoardChartCode,
  type BoardChartRow,
} from './board-chart-model'

const chartPalette = {
  ink: '#171717',
  muted: '#5d6663',
  paper: '#fbfcfa',
  white: '#ffffff',
  line: '#d9dfdc',
  aqua: '#9fd8d1',
  aquaDark: '#357f79',
  pink: '#ff2d6f',
  amber: '#c88010',
  green: '#24725a',
  grey: '#aeb8b5',
  blue: '#356f98',
}

const FONT = 'Arial, Helvetica, sans-serif'

const governancePositionColours = [chartPalette.aquaDark, chartPalette.pink, chartPalette.amber, chartPalette.blue]

function chartId(value: string) {
  return value.replace(/[^a-z0-9_-]+/gi, '-').toLowerCase()
}

function BoardChartFrame({ code, payload, idPrefix, children }: { code: BoardChartCode; payload: ReportingPayload; idPrefix: string; children: ReactNode }) {
  const definition = boardChartDefinition(code)
  const organisation = record(payload.organisation)
  const id = chartId(`${idPrefix}-${code}`)
  return <>
    <title id={`${id}-title`}>{definition.title}</title>
    <desc id={`${id}-description`}>{definition.subtitle} Position at {formatBoardDate(payload.asOf)}.</desc>
    <rect width="1920" height="1080" fill={chartPalette.paper} />
    <rect x="96" y="72" width="12" height="98" rx="6" fill={chartPalette.pink} />
    <text x="140" y="116" fontFamily={FONT} fontSize="48" fontWeight="700" fill={chartPalette.ink}>{definition.title}</text>
    <text x="140" y="160" fontFamily={FONT} fontSize="25" fill={chartPalette.muted}>{definition.subtitle}</text>
    {children}
    <line x1="100" y1="1005" x2="1820" y2="1005" stroke={chartPalette.line} strokeWidth="2" />
    <text x="100" y="1044" fontFamily={FONT} fontSize="18" fill={chartPalette.muted}>{textValue(organisation.name, 'Organisation')} · Position at {formatBoardDate(payload.asOf)}</text>
    <text x="1820" y="1044" textAnchor="end" fontFamily={FONT} fontSize="18" fontWeight="700" fill={chartPalette.muted}>SWELL AI BOARD REPORT</text>
  </>
}

function LabelLines({ label, x, centreY, maxChars = 48 }: { label: string; x: number; centreY: number; maxChars?: number }) {
  const labelLines = wrapBoardLabel(label, maxChars, 3)
  const lineHeight = 23
  const firstY = centreY - ((labelLines.length - 1) * lineHeight) / 2
  return <text x={x} y={firstY} fontFamily={FONT} fontSize="20" fontWeight="650" fill={chartPalette.ink}>
    {labelLines.map((line, index) => <tspan key={`${line}-${index}`} x={x} dy={index ? lineHeight : 0}>{line}</tspan>)}
  </text>
}

function HorizontalBars({ rows, series, stacked = false, emptyText }: {
  rows: BoardChartRow[]
  series: Array<{ label: string; colour: string }>
  stacked?: boolean
  emptyText: string
}) {
  if (!rows.length) return <text x="140" y="510" fontFamily={FONT} fontSize="28" fill={chartPalette.muted}>{emptyText}</text>
  const plotX = 720
  const plotWidth = 1000
  const top = 285
  const height = 640
  const rowHeight = height / Math.max(rows.length, 1)
  const maximum = niceMaximum(rows.map((row) => stacked ? row.values.reduce((sum, value) => sum + value, 0) : Math.max(...row.values, 0)))
  const tickFractions = maximum === 1 ? [0, 1] : [0, .5, 1]
  const barHeight = stacked ? Math.min(32, rowHeight * .42) : Math.min(22, rowHeight * .28)
  return <g data-chart-treatment="horizontal-bars">
    <g aria-label="Chart legend">
      {series.map((item, index) => <g key={item.label} transform={`translate(${720 + index * 260} 220)`}>
        <rect width="20" height="20" rx="4" fill={item.colour} />
        <text x="32" y="17" fontFamily={FONT} fontSize="19" fontWeight="700" fill={chartPalette.ink}>{item.label}</text>
      </g>)}
    </g>
    {tickFractions.map((fraction) => <g key={fraction}>
      <line x1={plotX + plotWidth * fraction} y1={top - 18} x2={plotX + plotWidth * fraction} y2={top + height} stroke={chartPalette.line} strokeWidth="1.5" />
      <text x={plotX + plotWidth * fraction} y={top + height + 34} textAnchor="middle" fontFamily={FONT} fontSize="18" fill={chartPalette.muted}>{Math.round(maximum * fraction)}</text>
    </g>)}
    {rows.map((row, rowIndex) => {
      const centreY = top + rowIndex * rowHeight + rowHeight / 2
      if (stacked) {
        let cursor = plotX
        const total = row.values.reduce((sum, value) => sum + value, 0)
        return <g key={`${row.label}-${rowIndex}`}>
          <LabelLines label={row.label} x={120} centreY={centreY} />
          <rect x={plotX} y={centreY - barHeight / 2} width={plotWidth} height={barHeight} rx="5" fill="#edf1ef" />
          {row.values.map((value, index) => {
            const width = maximum ? plotWidth * value / maximum : 0
            const x = cursor
            cursor += width
            return <g key={`${series[index]?.label ?? index}`}>
              <rect x={x} y={centreY - barHeight / 2} width={width} height={barHeight} fill={series[index]?.colour ?? chartPalette.grey} />
              {value > 0 && width >= 44 ? <text x={x + width / 2} y={centreY + 7} textAnchor="middle" fontFamily={FONT} fontSize="17" fontWeight="700" fill={index === 0 ? chartPalette.ink : chartPalette.white}>{value}</text> : null}
            </g>
          })}
          <text x={plotX + plotWidth * total / maximum + 18} y={centreY + 8} fontFamily={FONT} fontSize="20" fontWeight="700" fill={chartPalette.ink}>{total}</text>
        </g>
      }
      const groupHeight = barHeight * series.length + 8 * Math.max(0, series.length - 1)
      return <g key={`${row.label}-${rowIndex}`}>
        <LabelLines label={row.label} x={120} centreY={centreY} />
        {row.values.map((value, index) => {
          const y = centreY - groupHeight / 2 + index * (barHeight + 8)
          const width = plotWidth * value / maximum
          return <g key={`${series[index]?.label ?? index}`}>
            <rect x={plotX} y={y} width={plotWidth} height={barHeight} rx="5" fill="#edf1ef" />
            <rect x={plotX} y={y} width={width} height={barHeight} rx="5" fill={series[index]?.colour ?? chartPalette.blue} />
            <text x={plotX + width + 16} y={y + barHeight - 2} fontFamily={FONT} fontSize="20" fontWeight="700" fill={chartPalette.ink}>{value}</text>
          </g>
        })}
      </g>
    })}
  </g>
}

function GovernancePositionChart({ payload }: { payload: ReportingPayload }) {
  const rows = governancePositionChartRows(payload)
  const mode = governancePositionChartMode(rows)
  if (mode === 'empty') return <text x="140" y="510" fontFamily={FONT} fontSize="28" fill={chartPalette.muted}>No current AI Uses have a recorded governance position.</text>
  if (mode === 'horizontal-bars') return <HorizontalBars rows={rows} series={[{ label: 'AI Uses', colour: chartPalette.aquaDark }]} emptyText="No current AI Uses have a recorded governance position." />

  const total = rows.reduce((sum, row) => sum + numberValue(row.values[0]), 0)
  const centreX = 575
  const centreY = 575
  const outerRadius = 262
  const innerRadius = 158
  let angle = -90

  return <g data-chart-treatment="donut">
    {rows.map((row, index) => {
      const value = numberValue(row.values[0])
      const percentage = total ? Math.round(value / total * 100) : 0
      const startAngle = angle
      const endAngle = angle + (total ? value / total * 360 : 0)
      angle = endAngle
      return <path
        key={`${row.label}-${index}`}
        d={donutSegmentPath(centreX, centreY, outerRadius, innerRadius, startAngle, endAngle)}
        fill={governancePositionColours[index] ?? chartPalette.grey}
        data-donut-segment={row.label}
        aria-label={`${row.label}: ${value}, ${percentage}%`}
      />
    })}
    <text x={centreX} y={centreY - 4} textAnchor="middle" fontFamily={FONT} fontSize="76" fontWeight="750" fill={chartPalette.ink}>{total}</text>
    <text x={centreX} y={centreY + 40} textAnchor="middle" fontFamily={FONT} fontSize="22" fontWeight="700" fill={chartPalette.muted}>Current AI Uses</text>
    <g aria-label="Governance position legend">
      {rows.map((row, index) => {
        const value = numberValue(row.values[0])
        const percentage = total ? Math.round(value / total * 100) : 0
        const y = 330 + index * 145
        const lines = wrapBoardLabel(row.label, 38, 2)
        return <g key={`${row.label}-legend`} transform={`translate(1040 ${y})`}>
          <rect width="28" height="28" rx="6" fill={governancePositionColours[index] ?? chartPalette.grey} />
          <text x="48" y="22" fontFamily={FONT} fontSize="24" fontWeight="700" fill={chartPalette.ink}>
            {lines.map((line, lineIndex) => <tspan key={`${line}-${lineIndex}`} x="48" dy={lineIndex ? 29 : 0}>{line}</tspan>)}
          </text>
          <text x="48" y={50 + Math.max(0, lines.length - 1) * 29} fontFamily={FONT} fontSize="22" fill={chartPalette.muted}>{value}{' \u00b7 '}{percentage}%</text>
        </g>
      })}
    </g>
  </g>
}

function HorizontalChart({ code, payload }: { code: BoardChartCode; payload: ReportingPayload }) {
  if (code === 'business_area') return <HorizontalBars rows={businessAreaRows(payload)} series={[{ label: 'AI Uses', colour: chartPalette.blue }]} emptyText="No current AI Uses are recorded." />
  if (code === 'governance_position') return <GovernancePositionChart payload={payload} />
  if (code === 'strategic_alignment') return <HorizontalBars rows={strategicAlignmentRows(payload)} series={[{ label: 'AI Initiatives', colour: chartPalette.aquaDark }, { label: 'AI Uses', colour: chartPalette.pink }]} emptyText="Strategic alignment has not been recorded." />
  if (code === 'risk_categories') return <HorizontalBars stacked rows={riskCategoryRows(payload)} series={[
    { label: 'Within tolerance', colour: chartPalette.green },
    { label: 'Approaching tolerance', colour: chartPalette.amber },
    { label: 'Outside tolerance', colour: chartPalette.pink },
    { label: 'Not rated', colour: chartPalette.grey },
  ]} emptyText="No current AI risks are recorded." />
  if (code === 'governance_workload') return <HorizontalBars rows={governanceWorkloadRows(payload)} series={[{ label: 'Open items', colour: chartPalette.pink }]} emptyText="No open governance work is recorded." />
  return <HorizontalBars rows={materialChangeRows(payload)} series={[{ label: 'Material changes', colour: chartPalette.blue }]} emptyText="No material changes are recorded for this reporting period." />
}

function InitiativeMaturity({ payload, patternId }: { payload: ReportingPayload; patternId: string }) {
  const points = roadmapChartPoints(payload)
  if (!points.length) return <text x="140" y="510" fontFamily={FONT} fontSize="28" fill={chartPalette.muted}>Initiative timing has not been recorded.</text>
  const plotX = 150
  const plotY = 300
  const plotWidth = 1620
  const plotHeight = 610
  const maximum = niceMaximum(points.map((point) => point.total))
  const slot = plotWidth / points.length
  const barWidth = Math.min(92, slot * .58)
  return <g>
    <defs><pattern id={patternId} width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="14" height="14" fill="#eef1ef" /><line x1="0" y1="0" x2="0" y2="14" stroke={chartPalette.grey} strokeWidth="4" /></pattern></defs>
    <g aria-label="Chart legend">
      <rect x="150" y="218" width="20" height="20" rx="4" fill={chartPalette.aqua} /><text x="182" y="235" fontFamily={FONT} fontSize="19" fontWeight="700" fill={chartPalette.ink}>Planned</text>
      <rect x="315" y="218" width="20" height="20" rx="4" fill={chartPalette.pink} /><text x="347" y="235" fontFamily={FONT} fontSize="19" fontWeight="700" fill={chartPalette.ink}>Active</text>
      <rect x="458" y="218" width="20" height="20" rx="4" fill={`url(#${patternId})`} /><text x="490" y="235" fontFamily={FONT} fontSize="19" fontWeight="700" fill={chartPalette.ink}>Active classification unavailable</text>
    </g>
    {[0, .5, 1].map((fraction) => <g key={fraction}>
      <line x1={plotX} y1={plotY + plotHeight * (1 - fraction)} x2={plotX + plotWidth} y2={plotY + plotHeight * (1 - fraction)} stroke={chartPalette.line} strokeWidth="1.5" />
      <text x={plotX - 24} y={plotY + plotHeight * (1 - fraction) + 7} textAnchor="end" fontFamily={FONT} fontSize="18" fill={chartPalette.muted}>{Math.round(maximum * fraction)}</text>
    </g>)}
    {points.map((point, index) => {
      const x = plotX + slot * index + (slot - barWidth) / 2
      const totalHeight = plotHeight * point.total / maximum
      const plannedHeight = plotHeight * point.planned / maximum
      const activeHeight = point.active === null ? 0 : plotHeight * point.active / maximum
      const base = plotY + plotHeight
      return <g key={`${point.quarterStart}-${index}`} data-quality={point.quality}>
        {point.active === null
          ? <rect x={x} y={base - totalHeight} width={barWidth} height={totalHeight} rx="7" fill={`url(#${patternId})`} />
          : <><rect x={x} y={base - plannedHeight} width={barWidth} height={plannedHeight} rx="7" fill={chartPalette.aqua} /><rect x={x} y={base - plannedHeight - activeHeight} width={barWidth} height={activeHeight} rx="7" fill={chartPalette.pink} /></>}
        <text x={x + barWidth / 2} y={base - totalHeight - 16} textAnchor="middle" fontFamily={FONT} fontSize="21" fontWeight="700" fill={chartPalette.ink}>{point.total}</text>
        <text x={x + barWidth / 2} y={base + 38} textAnchor="middle" fontFamily={FONT} fontSize="18" fill={chartPalette.muted}>{point.label}</text>
      </g>
    })}
  </g>
}

function RoadmapActivity({ payload }: { payload: ReportingPayload }) {
  const points = roadmapActivityPoints(payload)
  if (!points.length) return <text x="140" y="510" fontFamily={FONT} fontSize="28" fill={chartPalette.muted}>Initiative timing has not been recorded.</text>
  const plotX = 150
  const plotY = 300
  const plotWidth = 1620
  const plotHeight = 610
  const maximum = niceMaximum(points.map((point) => point.total))
  const slot = plotWidth / points.length
  const barWidth = Math.min(92, slot * .58)
  return <g>
    <g aria-label="Chart legend">
      <rect x="150" y="218" width="20" height="20" rx="4" fill={chartPalette.pink} /><text x="182" y="235" fontFamily={FONT} fontSize="19" fontWeight="700" fill={chartPalette.ink}>Starting</text>
      <rect x="315" y="218" width="20" height="20" rx="4" fill={chartPalette.aquaDark} /><text x="347" y="235" fontFamily={FONT} fontSize="19" fontWeight="700" fill={chartPalette.ink}>Continuing</text>
    </g>
    {[0, .5, 1].map((fraction) => <g key={fraction}>
      <line x1={plotX} y1={plotY + plotHeight * (1 - fraction)} x2={plotX + plotWidth} y2={plotY + plotHeight * (1 - fraction)} stroke={chartPalette.line} strokeWidth="1.5" />
      <text x={plotX - 24} y={plotY + plotHeight * (1 - fraction) + 7} textAnchor="end" fontFamily={FONT} fontSize="18" fill={chartPalette.muted}>{Math.round(maximum * fraction)}</text>
    </g>)}
    {points.map((point, index) => {
      const x = plotX + slot * index + (slot - barWidth) / 2
      const startingHeight = plotHeight * point.starting / maximum
      const continuingHeight = plotHeight * point.continuing / maximum
      const totalHeight = startingHeight + continuingHeight
      const base = plotY + plotHeight
      return <g key={`${point.quarterStart}-${index}`}>
        <rect x={x} y={base - continuingHeight} width={barWidth} height={continuingHeight} rx="7" fill={chartPalette.aquaDark} />
        <rect x={x} y={base - continuingHeight - startingHeight} width={barWidth} height={startingHeight} rx="7" fill={chartPalette.pink} />
        <text x={x + barWidth / 2} y={base - totalHeight - 16} textAnchor="middle" fontFamily={FONT} fontSize="21" fontWeight="700" fill={chartPalette.ink}>{point.total}</text>
        <text x={x + barWidth / 2} y={base + 38} textAnchor="middle" fontFamily={FONT} fontSize="18" fill={chartPalette.muted}>{point.label}</text>
      </g>
    })}
  </g>
}

function RiskMatrix({ payload }: { payload: ReportingPayload }) {
  const matrix = record(record(payload.risk).matrix)
  const cells = records(matrix.cells)
  const startX = 330
  const startY = 335
  const cellWidth = 350
  const cellHeight = 180
  const lookup = (likelihood: number, consequence: number) => cells
    .filter((cell) => numberValue(cell.likelihoodLevel) === likelihood && numberValue(cell.consequenceLevel) === consequence)
    .reduce((sum, cell) => sum + numberValue(cell.riskCount), 0)
  const band = (likelihood: number, consequence: number) => likelihood + consequence >= 5 ? 'Outside tolerance' : likelihood + consequence >= 4 ? 'Approaching tolerance' : 'Within tolerance'
  const colour = (value: string) => value === 'Outside tolerance' ? chartPalette.pink : value === 'Approaching tolerance' ? chartPalette.amber : chartPalette.green
  return <g>
    <text x="855" y="255" textAnchor="middle" fontFamily={FONT} fontSize="22" fontWeight="700" fill={chartPalette.muted}>CONSEQUENCE</text>
    <text x="158" y="610" textAnchor="middle" transform="rotate(-90 158 610)" fontFamily={FONT} fontSize="22" fontWeight="700" fill={chartPalette.muted}>LIKELIHOOD</text>
    {['Low', 'Medium', 'High'].map((label, index) => <text key={label} x={startX + index * cellWidth + cellWidth / 2} y="310" textAnchor="middle" fontFamily={FONT} fontSize="22" fontWeight="700" fill={chartPalette.muted}>{label}</text>)}
    {[3, 2, 1].map((likelihood, row) => <g key={likelihood}>
      <text x="300" y={startY + row * cellHeight + cellHeight / 2 + 8} textAnchor="end" fontFamily={FONT} fontSize="22" fontWeight="700" fill={chartPalette.muted}>{likelihood === 3 ? 'High' : likelihood === 2 ? 'Medium' : 'Low'}</text>
      {[1, 2, 3].map((consequence, column) => {
        const state = band(likelihood, consequence)
        const count = lookup(likelihood, consequence)
        return <g key={consequence} data-risk-cell={`${likelihood}-${consequence}`} data-risk-band={state.toLowerCase().replaceAll(' ', '-')}>
          <rect x={startX + column * cellWidth} y={startY + row * cellHeight} width={cellWidth - 14} height={cellHeight - 14} rx="16" fill={colour(state)} />
          <text x={startX + column * cellWidth + (cellWidth - 14) / 2} y={startY + row * cellHeight + 68} textAnchor="middle" fontFamily={FONT} fontSize="20" fontWeight="700" fill={state === 'Approaching tolerance' ? chartPalette.ink : chartPalette.white}>{state}</text>
          <text x={startX + column * cellWidth + (cellWidth - 14) / 2} y={startY + row * cellHeight + 126} textAnchor="middle" fontFamily={FONT} fontSize="48" fontWeight="700" fill={state === 'Approaching tolerance' ? chartPalette.ink : chartPalette.white}>{count}</text>
        </g>
      })}
    </g>)}
    <g transform="translate(1450 360)">
      <text fontFamily={FONT} fontSize="20" fontWeight="700" fill={chartPalette.muted}>CURRENT RISKS</text>
      <text y="72" fontFamily={FONT} fontSize="68" fontWeight="700" fill={chartPalette.ink}>{numberValue(matrix.totalCurrent)}</text>
      <text y="138" fontFamily={FONT} fontSize="20" fontWeight="700" fill={chartPalette.muted}>NOT YET RATED</text>
      <text y="210" fontFamily={FONT} fontSize="68" fontWeight="700" fill={chartPalette.ink}>{numberValue(matrix.notRatedCount)}</text>
    </g>
  </g>
}

export function BoardChartSvg({ code, payload, idPrefix = 'board-chart', x, y, width, height }: {
  code: BoardChartCode
  payload: ReportingPayload
  idPrefix?: string
  x?: number
  y?: number
  width?: number
  height?: number
}) {
  const id = chartId(`${idPrefix}-${code}`)
  return <svg
    x={x}
    y={y}
    width={width ?? 1920}
    height={height ?? 1080}
    viewBox="0 0 1920 1080"
    role="img"
    aria-labelledby={`${id}-title ${id}-description`}
    data-board-chart={code}
    data-export-width="1920"
    data-export-height="1080"
    preserveAspectRatio="xMidYMid meet"
  >
    <BoardChartFrame code={code} payload={payload} idPrefix={idPrefix}>
      {code === 'initiative_maturity' ? <InitiativeMaturity payload={payload} patternId={`${id}-unavailable`} /> : null}
      {code === 'roadmap_activity' ? <RoadmapActivity payload={payload} /> : null}
      {code === 'risk_matrix' ? <RiskMatrix payload={payload} /> : null}
      {!['initiative_maturity', 'roadmap_activity', 'risk_matrix'].includes(code) ? <HorizontalChart code={code} payload={payload} /> : null}
    </BoardChartFrame>
  </svg>
}

export function boardChartFileName(code: BoardChartCode, asOf: unknown): string {
  const title = boardChartDefinition(code).title.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '')
  const date = new Date(textValue(asOf))
  const dateLabel = Number.isNaN(date.getTime()) ? 'date-not-recorded' : date.toISOString().slice(0, 10)
  return `Swell_${title}_${dateLabel}.png`
}
